// src/hooks/useMatches.ts
import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  Timestamp,
  doc,
  deleteDoc,
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';

export interface Match {
  id: string;
  source: string;
  matched_at: Date | null;
  super: boolean;
  title: string;
  description: string;
  image_url?: string | null;
  location: {
    lat: number;
    lng: number;
    address?: string | null;
  };
  tags: string[];
  place_id?: string;
  chat_prompt: string;
}

export const useMatches = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }

    const ref = collection(db, 'users', user.uid, 'matches');
    const q = query(ref, orderBy('matched_at', 'desc'));

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const out: Match[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          out.push({
            id: docSnap.id,
            source: data.source || 'guide',
            matched_at:
              data.matched_at instanceof Timestamp
                ? data.matched_at.toDate()
                : null,
            super: !!data.super,
            title: data.title || '',
            description: data.description || '',
            image_url: data.image_url,
            location: data.location || { lat: 0, lng: 0 },
            tags: Array.isArray(data.tags) ? data.tags : [],
            place_id: data.place_id,
            chat_prompt: data.chat_prompt || '',
          });
        });
        setMatches(out);
        setIsLoading(false);
      },
      (err) => {
        console.warn('Matches listener error:', err);
        setIsLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const unmatch = useCallback(async (matchId: string) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'matches', matchId));
    } catch (err) {
      console.warn('Unmatch failed:', err);
    }
  }, []);

  return { matches, isLoading, unmatch };
};
